import { parseIsoDate, safeParseIsoDate } from '@liftai/shared'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

type Resolution = 'date' | 'datetime'
const DEFAULT_ERROR_FALLBACK = 'Invalid date'
const DEFAULT_EMPTY_FALLBACK = 'Not set'

export const formatDate = (
  dateString: string | Date,
  resolution: Resolution = 'datetime',
  errorFallback = DEFAULT_ERROR_FALLBACK,
): string => {
  try {
    if (resolution === 'date') {
      const date =
        typeof dateString === 'string' ? parseIsoDate(dateString) : dateString
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })
    } else {
      const date =
        typeof dateString === 'string' ? new Date(dateString) : dateString
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    }
  } catch {
    return errorFallback
  }
}

export function formatDateRelative(
  dateString: string | Date,
  errorFallback = DEFAULT_ERROR_FALLBACK,
) {
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString)
    const now = new Date()

    // If it's today, show "Today, 10:30AM"
    if (date.toDateString() === now.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
    }

    // If it's yesterday, show "Yesterday, 10:30AM"
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
    }
    // Otherwise show the full date with time: "Dec 12, 2024 10:30AM"
    return `${date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })} ${date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`
  } catch {
    return errorFallback
  }
}

/**
 * Formats any date type (ISO string, Timestamp, or Date) to localized date string
 * Handles ISO date strings (YYYY-MM-DD), Firestore Timestamps, and Date objects
 * @param date - Date in any supported format
 * @returns Localized date string (MM/DD/YYYY) or "Invalid date"
 */
export function formatAnyDate(
  date: string | Date | null | undefined,
  errorFallback = DEFAULT_ERROR_FALLBACK,
  emptyFallback = DEFAULT_EMPTY_FALLBACK,
): string {
  if (!date) {
    return emptyFallback
  }

  try {
    const jsDate = date instanceof Date ? date : safeParseIsoDate(date)
    if (!jsDate) {
      return errorFallback
    }
    return jsDate.toLocaleDateString()
  } catch {
    return errorFallback
  }
}

/**
 * Formats any date type to localized date and time string
 * @param date - Date in any supported format (Timestamp or Date)
 * @returns Localized date/time string or "Invalid date"
 */
export function formatAnyDateTime(
  date: Date | null | undefined,
  errorFallback = DEFAULT_ERROR_FALLBACK,
  emptyFallback = DEFAULT_EMPTY_FALLBACK,
): string {
  if (!date) {
    return emptyFallback
  }

  try {
    return date.toLocaleString()
  } catch {
    return errorFallback
  }
}

/**
 * Gets formatted status information for a maintenance item
 * Utility function for displaying maintenance item status with dates
 */
export function getMaintenanceItemStatus(item: {
  status: 'pending' | 'completed' | 'overdue'
  completedBy: string | null
  completedDate: Date | null
  dueDate?: Date | string
}) {
  return {
    completed: item.status === 'completed',
    completedBy: item.completedBy,
    completedAt: item.completedDate
      ? formatAnyDateTime(item.completedDate)
      : null,
    dueDate: item.dueDate ? formatAnyDate(item.dueDate) : undefined,
    isOverdue: item.status === 'overdue',
  }
}
